import React, { forwardRef, ForwardRefRenderFunction } from "react";
//#region types
import { DeviationFormProps } from "models/propTypes/salaryDeviationPropTypes";
import {
  AutoCompleteExtraProps,
  DateFormFieldProps,
  EditableTableTabFieldProps,
  FormFieldDefinitionType,
  FormHandles,
  NumberFormatFieldExtraProps,
} from "models/propTypes/genericFormProps";
import { FormEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { SalaryDeviationFormTypes } from "models/formTypes";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { GenericForm } from "components/common";
import { useCompanyService, useSearchService } from "serviceHooks";
//#endregion
import { CountryHelper, LocalizationHelperInstance } from "utils/helpers";

const CreateAllowanceForm: ForwardRefRenderFunction<FormHandles, DeviationFormProps> = (props, ref) => {
  const companyService = useCompanyService();
  const searchService = useSearchService();

  const {
    isSalaryAdmin,
    isBusinessUnitManagerWithSalaryRights,
    isDeviationAdmin,
    datesChangeHandler,
    allowedDeviationTypes,
    isNew,
  } = props;

  const form = React.useMemo(
    () => (
      <GenericForm<SalaryDeviationFormTypes.CreateAllowance>
        formRef={ref}
        {...props}
        fields={{
          deviationType: {
            controlLabel: TranslationKeys.SalaryDeviation.TypeOfAllowance,
            label: " ",
            type: FormEnums.FormFieldType.CHOICE,
            extraProps: {
              items: allowedDeviationTypes.map((type) => ({
                id: type,
                value: LocalizationHelperInstance.getFormattedMessage(`SalaryDeviation.Type_${type}`),
              })),
            },
            disabled: props.disabled,
          },
          personName: {
            controlLabel: " ",
            label: TranslationKeys.Common.Employee,
            type: FormEnums.FormFieldType.AUTOCOMPLETE,
            disabled:
              !(isSalaryAdmin || isBusinessUnitManagerWithSalaryRights || isDeviationAdmin) || props.disabled || !isNew,
            icon: IconDefinitionConstants.WUser,
            extraProps: {
              items: (searchStr: string) =>
                companyService.SearchPersonsAutoComplete(
                  searchStr,
                  false,
                  true,
                  isBusinessUnitManagerWithSalaryRights && !isSalaryAdmin
                ),
              referenceFieldName: FormFieldConstants.SalaryDeviation.CreateAllowance.PersonId,
            } as AutoCompleteExtraProps<SalaryDeviationFormTypes.CreateAllowance>,
          },
          customName: {
            label: TranslationKeys.SalaryDeviation.NameOfDeviation,
            type: FormEnums.FormFieldType.TEXT,
            hidden: (values: SalaryDeviationFormTypes.CreateAllowance) =>
              values.deviationType !== WintSuperkollAPIModel.SalaryDeviationType.CustomDeviationCompensation,
            disabled: props.disabled,
            descriptionText: TranslationKeys.Allowance.NameOfDeviationDescription,
          },
          yearAndMonth: {
            controlLabel: " ",
            label: TranslationKeys.Common.MonthPascal,
            type: FormEnums.FormFieldType.MONTH,
            hidden: (values: SalaryDeviationFormTypes.CreateAllowance) =>
              [
                WintSuperkollAPIModel.SalaryDeviationType.Allowance,
                WintSuperkollAPIModel.SalaryDeviationType.AllowanceForeign,
              ].some((t) => t === values.deviationType),
            disabled: props.disabled,
            descriptionText: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Allowance.YearAndMonthDescription
            ),
          },
          mileage: {
            label: " ",
            type: FormEnums.FormFieldType.HORIONTALFIELDGROUP,
            hidden: (values: SalaryDeviationFormTypes.CreateAllowance) =>
              values.deviationType !== WintSuperkollAPIModel.SalaryDeviationType.Mileage,
            innerFields: {
              mileage: {
                controlLabel: " ",
                label: TranslationKeys.SalaryDeviation.Mileage,
                type: FormEnums.FormFieldType.NUMBER,
                extraProps: {
                  suffix: "km",
                } as NumberFormatFieldExtraProps,
                disabled: props.disabled,
                widthColumnCount: 5,
              },
              vehicleTypeName: {
                controlLabel: " ",
                label: TranslationKeys.SalaryDeviation.TypeOfCar,
                type: FormEnums.FormFieldType.AUTOCOMPLETE,
                extraProps: {
                  referenceFieldName: FormFieldConstants.SalaryDeviation.CreateAllowance.VehicleType,
                  items: searchService.salaryDeviationVehicleTypes,
                } as AutoCompleteExtraProps<SalaryDeviationFormTypes.CreateAllowance>,
                disabled: props.disabled,
                widthColumnCount: 7,
              },
            },
          },
          workHours: {
            label: "",
            type: FormEnums.FormFieldType.HORIONTALFIELDGROUP,
            hidden: (values: SalaryDeviationFormTypes.CreateAllowance) =>
              ![
                WintSuperkollAPIModel.SalaryDeviationType.WithdrawalCompensation,
                WintSuperkollAPIModel.SalaryDeviationType.DepositedCompensation,
                WintSuperkollAPIModel.SalaryDeviationType.WorkedHours,
              ].some((t) => t === values.deviationType),
            innerFields: {
              workHours: {
                label: TranslationKeys.Common.HoursPascal,
                type: FormEnums.FormFieldType.NUMBER,
                extraProps: {
                  max: 8,
                  allowNegative: false,
                } as NumberFormatFieldExtraProps,
                disabled: props.disabled,
              },
              workMinutes: {
                label: TranslationKeys.Common.MinutesPascal,
                type: FormEnums.FormFieldType.NUMBER,
                extraProps: {
                  max: 59,
                  allowNegative: false,
                } as NumberFormatFieldExtraProps,
                disabled: props.disabled,
              },
            },
          },
          sumDescriptions: {
            label: " ",
            controlLabel: " ",
            type: FormEnums.FormFieldType.EDITABLETABLE,
            disabled: props.disabled,
            hidden: (values: SalaryDeviationFormTypes.CreateAllowance) =>
              ![
                WintSuperkollAPIModel.SalaryDeviationType.Bonus,
                WintSuperkollAPIModel.SalaryDeviationType.CommissionFee,
                WintSuperkollAPIModel.SalaryDeviationType.CommissionVacationPaid,
                WintSuperkollAPIModel.SalaryDeviationType.CommissionVacationSaved,
                WintSuperkollAPIModel.SalaryDeviationType.WithdrawalsSavedVacation,
                WintSuperkollAPIModel.SalaryDeviationType.CustomDeviationCompensation,
              ].some((t) => t === values.deviationType),
            innerFields: {
              sum: {
                label: TranslationKeys.Common.Amount,
                type: FormEnums.FormFieldType.CURRENCY,
                widthColumnCount: 4,
                extraProps: {
                  allowNegative: true,
                },
              },
              description: {
                label: TranslationKeys.SalaryDeviation.Description,
                type: FormEnums.FormFieldType.TEXT,
              },
            } as FormFieldDefinitionType<SalaryDeviationFormTypes.SumDescription>,
            extraProps: {
              defaultRow: { sum: undefined, description: undefined },
              idFieldKey: FormFieldConstants.SalaryDeviation.HoursByDay.Id,
            } as EditableTableTabFieldProps<SalaryDeviationFormTypes.SumDescription>,
          },
          countryName: {
            controlLabel: " ",
            label: TranslationKeys.Common.Country,
            type: FormEnums.FormFieldType.AUTOCOMPLETE,
            extraProps: {
              items: CountryHelper.getCountryAutoCompleteList(),
              referenceFieldName: FormFieldConstants.SalaryDeviation.CreateAllowance.CountryCode,
            } as AutoCompleteExtraProps<SalaryDeviationFormTypes.CreateAllowance>,
            hidden: (values: SalaryDeviationFormTypes.CreateAllowance) =>
              values.deviationType !== WintSuperkollAPIModel.SalaryDeviationType.AllowanceForeign,
            disabled: props.disabled,
          },
          fromDate: {
            label: " ",
            type: FormEnums.FormFieldType.HORIONTALFIELDGROUP,
            hidden: (values: SalaryDeviationFormTypes.CreateAllowance) =>
              ![
                WintSuperkollAPIModel.SalaryDeviationType.Allowance,
                WintSuperkollAPIModel.SalaryDeviationType.AllowanceForeign,
              ].some((t) => t === values.deviationType),
            innerFields: {
              fromDate: {
                label: TranslationKeys.Common.DateFrom,
                type: FormEnums.FormFieldType.DATE,
                onChangeHandler: datesChangeHandler,
                extraProps: {
                  maxDateRefFieldName: FormFieldConstants.SalaryDeviation.CreateAllowance.ToDate,
                } as DateFormFieldProps<SalaryDeviationFormTypes.CreateAllowance>,
                disabled: props.disabled,
              },
              fromTime: {
                label: TranslationKeys.SalaryDeviation.FromTime,
                type: FormEnums.FormFieldType.TIME,
                disabled: props.disabled,
              },
            },
          },
          toDate: {
            label: " ",
            type: FormEnums.FormFieldType.HORIONTALFIELDGROUP,
            hidden: (values: SalaryDeviationFormTypes.CreateAllowance) =>
              ![
                WintSuperkollAPIModel.SalaryDeviationType.Allowance,
                WintSuperkollAPIModel.SalaryDeviationType.AllowanceForeign,
              ].some((t) => t === values.deviationType),
            innerFields: {
              toDate: {
                label: TranslationKeys.Common.DateTo,
                type: FormEnums.FormFieldType.DATE,
                onChangeHandler: datesChangeHandler,
                extraProps: {
                  minDateRefFieldName: FormFieldConstants.SalaryDeviation.CreateAllowance.FromDate,
                } as DateFormFieldProps<SalaryDeviationFormTypes.CreateAllowance>,
                disabled: props.disabled,
              },
              toTime: {
                label: TranslationKeys.SalaryDeviation.ToTime,
                type: FormEnums.FormFieldType.TIME,
                disabled: props.disabled,
              },
            },
          },
          mealDeductions: {
            label: " ",
            controlLabel: TranslationKeys.SalaryDeviation.CostDeduction,
            type: FormEnums.FormFieldType.EDITABLETABLE,
            disabled: props.disabled,
            descriptionText: TranslationKeys.Allowance.MealDescription,
            hidden: (values: SalaryDeviationFormTypes.CreateAllowance) =>
              ![
                WintSuperkollAPIModel.SalaryDeviationType.Allowance,
                WintSuperkollAPIModel.SalaryDeviationType.AllowanceForeign,
              ].some((t) => t === values.deviationType),
            innerFields: {
              day: {
                controlLabel: TranslationKeys.Allowance.MealTitle,
                label: TranslationKeys.Common.DayPascal,
                type: FormEnums.FormFieldType.DATE,
                extraProps: {
                  minDateRefFieldName: FormFieldConstants.SalaryDeviation.CreateAllowance.FromDate,
                  maxDateRefFieldName: FormFieldConstants.SalaryDeviation.CreateAllowance.ToDate,
                } as DateFormFieldProps<SalaryDeviationFormTypes.CreateAllowance>,
              },
              breakfast: {
                label: TranslationKeys.SalaryDeviation.Breakfast,
                type: FormEnums.FormFieldType.CHECKBOX,
              },
              lunch: {
                label: TranslationKeys.SalaryDeviation.Lunch,
                type: FormEnums.FormFieldType.CHECKBOX,
              },
              dinner: {
                label: TranslationKeys.SalaryDeviation.Dinner,
                type: FormEnums.FormFieldType.CHECKBOX,
              },
            } as FormFieldDefinitionType<SalaryDeviationFormTypes.SalaryDeviationMeal>,
            extraProps: {
              defaultRow: { day: undefined, breakfast: false, lunch: false, dinner: false },
              showColumnHeaders: true,
              headersNoWrap: true,
              idFieldKey: FormFieldConstants.SalaryDeviation.HoursByDay.Id,
            },
          },
          comment: {
            controlLabel: " ",
            label: TranslationKeys.Common.Comment,
            type: FormEnums.FormFieldType.TEXTAREA,
            icon: IconDefinitionConstants.WCommentDots,
            disabled: props.disabled,
            descriptionText: TranslationKeys.Allowance.CommentDescription,
          },
        }}
        hideSuccess
        formGridLayout={{ message: false }}
      />
    ),
    [props.defaultValues]
  );

  return form;
};

export default forwardRef(CreateAllowanceForm);
